import { EonUiIcon, EonUiButton } from "@eon-ui/eon-ui-components-react";
import { SimpleOutsideClick } from "@me8eon/outside_click";
import { useTranslation } from "@me8eon/translation";
import { useState, useMemo } from "react";
import useOnboardingPortalStore from "./OnboardingPortal.Store";
import { useUserData } from "@me8eon/react_authentication";
import { ROLE_ONBOARDING_PORTAL_REVIEWER } from "./OnboardingPortal.constants";

export const HeaderOption = ({ dropDownLabel, label, options }: any) => {
    const [open, setOpen] = useState(false);
    return (
        <SimpleOutsideClick onClickOutside={() => setOpen(false)}>
            <div
                className="onboarding-portal--header--dropdown-overlay"
                onMouseLeave={() => setOpen(false)}
            >
                <HeaderItem
                    {...dropDownLabel}
                    onClick={() => setOpen(!open)}
                    onHoverIn={() => {
                        setOpen(true);
                    }}
                />
                <div className="onboarding-portal--header--dropdown-overlay--overlay-wrapper">
                    <div
                        className={`onboarding-portal--header--dropdown-overlay--overlay-wrapper--dropdown-body ${open ? "open" : ""}`}
                    >
                        {open && label && (
                            <div
                                style={{
                                    padding: "6px 12px",
                                    fontWeight: 500,
                                    color: "#404040",
                                }}
                                onClick={() => setOpen(!open)}
                            >
                                {label}
                            </div>
                        )}
                        {options.map((option: any, index: number) => {
                            return <HeaderItem {...option} key={index} />;
                        })}
                    </div>
                </div>
            </div>
        </SimpleOutsideClick>
    );
};

const HeaderItem = ({
    onClick,
    onHoverIn,
    onHoverOut,
    text,
    iconLeft,
    iconRight,
}: {
    onClick: () => void;
    onHoverIn?: () => void;
    onHoverOut?: () => void;
    text: string;
    iconLeft?: string;
    iconRight?: string;
}) => {
    return (
        <div
            className="onboarding-portal--header--navigation--item"
            onClick={onClick}
            onMouseEnter={onHoverIn}
            onMouseLeave={onHoverOut}
        >
            {iconLeft && (
                <EonUiIcon name={iconLeft} size="small" scheme="red500" />
            )}
            <span style={{ flexShrink: 0 }}>{text}</span>
            {iconRight && <EonUiIcon name={iconRight} size="small" />}
        </div>
    );
};

export const OnboardingPortalHeader = () => {
    const { actualRoles } = useUserData();
    const t = useTranslation();
    const { showModule, setShowModule, setCreateNewQnA } =
        useOnboardingPortalStore();
    const heading = useMemo(() => {
        switch (showModule) {
            case "home":
                return t("onboardingPortal.heading");
            case "edit-apps":
            case "manage-apps":
                return t("onboardingPortal.manageApp.heading");
            case "edit-qna":
            case "manage-qna":
                return t("onboardingPortal.manageQnA.heading");
            case "default-apps":
                return t("onboardingPortal.defaultApps.heading");
            case "onboard-apps":
                return t("onboardingPortal.appOnboardingForm.formHeading");
            case "onboard-qna":
                return t("onboardingPortal.qnaOnboardingForm.formHeading");
            case "review-dashboard":
                return t("onboardingPortal.reviewDashboard.heading");
            case "review-apps":
                return t("onboardingPortal.reviewApps.heading");
            case "review-qna":
                return t("onboardingPortal.reviewQnA.heading");
            default:
                return "";
        }
    }, [t, showModule]);
    const subHeading = useMemo(() => {
        switch (showModule) {
            case "home":
                return t("onboardingPortal.subHeading");
            case "edit-apps":
            case "manage-apps":
                return t("onboardingPortal.manageApp.subHeading");
            case "edit-qna":
            case "manage-qna":
                return t("onboardingPortal.manageQnA.subHeading");
            case "default-apps":
                return t("onboardingPortal.defaultApps.subHeading");
            case "review-dashboard":
                return t("onboardingPortal.reviewDashboard.subHeading");
            case "review-apps":
                return t("onboardingPortal.reviewApps.subHeading");
            case "review-qna":
                return t("onboardingPortal.reviewQnA.subHeading");
            default:
                return "";
        }
    }, [t, showModule]);
    return (
        <>
            <div className="onboarding-portal--header">
                <div className="onboarding-portal--header--navigation">
                    <div
                        className="onboarding-portal--header--navigation--back-button"
                        onClick={() => window.history.back()}
                    >
                        <EonUiIcon name="chevron_small_left" size="small" />{" "}
                        <span>Back to MyHelp</span>
                    </div>
                    <HeaderItem
                        onClick={() => setShowModule("home")}
                        text={t("onboardingPortal.modules.home")}
                        iconLeft="home"
                    />
                    <HeaderOption
                        dropDownLabel={{
                            text: t("onboardingPortal.modules.apps"),
                            iconLeft: "modular",
                            iconRight: "chevron_small_down",
                        }}
                        label={t("onboardingPortal.modules.appsLabel")}
                        options={[
                            {
                                text: t("onboardingPortal.modules.onboardApp"),
                                iconLeft: "add",
                                onClick: () => setShowModule("onboard-apps"),
                            },
                            {
                                text: t("onboardingPortal.modules.manageApp"),
                                iconLeft: "dashboard_alt_2",
                                onClick: () => setShowModule("manage-apps"),
                            },
                            {
                                text: t("onboardingPortal.modules.defaultApps"),
                                iconLeft: "all_in_one_alt",
                                onClick: () => setShowModule("default-apps"),
                            },
                        ]}
                    />
                    <HeaderOption
                        dropDownLabel={{
                            text: t("onboardingPortal.modules.qna"),
                            iconLeft: "question_mark",
                            iconRight: "chevron_small_down",
                        }}
                        label={t("onboardingPortal.modules.qnaLabel")}
                        options={[
                            {
                                text: t("onboardingPortal.modules.onboardQnA"),
                                iconLeft: "add",
                                onClick: () => setShowModule("onboard-qna"),
                            },
                            {
                                text: t("onboardingPortal.modules.manageQnA"),
                                iconLeft: "dashboard_alt_2",
                                onClick: () => setShowModule("manage-qna"),
                            },
                        ]}
                    />
                    {actualRoles.includes(ROLE_ONBOARDING_PORTAL_REVIEWER) && (
                        <HeaderOption
                            dropDownLabel={{
                                text: t("onboardingPortal.modules.review"),
                                iconLeft: "value_proposition",
                                iconRight: "chevron_small_down",
                            }}
                            label={t("onboardingPortal.modules.reviewLabel")}
                            options={[
                                {
                                    text: t(
                                        "onboardingPortal.modules.reviewDashboard"
                                    ),
                                    iconLeft: "dashboard",
                                    onClick: () =>
                                        setShowModule("review-dashboard"),
                                },
                                {
                                    text: t(
                                        "onboardingPortal.modules.reviewApps"
                                    ),
                                    iconLeft: "add",
                                    onClick: () => setShowModule("review-apps"),
                                },
                                {
                                    text: t(
                                        "onboardingPortal.modules.reviewQnA"
                                    ),
                                    iconLeft: "question_mark",
                                    onClick: () => setShowModule("review-qna"),
                                },
                            ]}
                        />
                    )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <h1 className="onboarding-portal--header--heading">
                        {heading}
                    </h1>
                    {showModule === "onboard-qna" && (
                        <div style={{ marginLeft: "auto" }}>
                            <EonUiButton
                                icon="add"
                                iconPosition="left"
                                text={t(
                                    "onboardingPortal.qnaOnboardingForm.addNewQnABtn"
                                )}
                                rank="secondary"
                                size="small"
                                onClick={() => {
                                    setShowModule("onboard-qna");
                                    setCreateNewQnA();
                                }}
                            />
                        </div>
                    )}
                </div>
                {subHeading && (
                    <p className="onboarding-portal--header--sub-heading">
                        {subHeading}
                    </p>
                )}
            </div>
        </>
    );
};
